<script setup lang="ts">
import { secondsToHHMMSS } from '~/utils/video'
import type { TVimeoData } from '~/types/app.type'

const props = defineProps({
  videoId: {
    type: [Number, String],
    required: true,
  },
  autoplay: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: '',
  },
  isPreview: {
    type: Boolean,
    default: false,
  },
  isFree: {
    type: Boolean,
    default: false,
  },
  categories: {
    type: Array<{ name: string; id: number; slug: string }>,
    default: () => [],
  },
})

type TVimeoEventParams = {
  duration: number
  seconds: number
  percent: number
}

const { $vimeo, $rudderstack } = useNuxtApp()
const vimeoData = ref<TVimeoData>(null)
const player = ref()
const emit = defineEmits(['endedVideo', 'videoStarted', 'videoPaused'])

const onVideoEnded = (params: TVimeoEventParams) => {
  if (!params) return

  emit('endedVideo')
  $rudderstack.trackVideoEnded({
    video_id: props.videoId,
    video_title: vimeoData.value?.title,
    video_duration: secondsToHHMMSS(vimeoData.value?.duration),
    video_type: props.isFree || props.isPreview ? 'Free' : 'Paid',
    video_categories: props.categories.map((c) => c.name),
  })
}

const onVideoInit = (params: TVimeoEventParams) => {
  if (!params) return

  emit('videoStarted')
  $rudderstack.trackVideoStarted({
    video_id: props.videoId,
    video_title: vimeoData.value?.title,
    video_duration: secondsToHHMMSS(vimeoData.value?.duration),
    progress_percentage: `${params.percent * 100}`,
    video_type: props.isFree ? 'Free' : 'Paid',
    video_categories: props.categories.map((c) => c.name),
  })
}

const onVideoProgress = (params: TVimeoEventParams) => {
  if (!params) return

  emit('videoPaused', params)
  $rudderstack.trackVideoProgress({
    video_id: props.videoId,
    video_title: vimeoData.value?.title,
    video_duration: secondsToHHMMSS(vimeoData.value?.duration),
    progress_percentage: `${params.percent * 100}`,
    video_type: props.isFree ? 'Free' : 'Paid',
    video_categories: props.categories.map((c) => c.name),
  })
}

onMounted(async () => {
  const vimeoPlayer = $vimeo.init(player.value)
  vimeoData.value = await $vimeo.getVideoData(props.videoId)

  vimeoPlayer.on('ended', onVideoEnded)
  vimeoPlayer.on('play', onVideoInit)
  vimeoPlayer.on('progress', onVideoProgress)
})

const routeVideo = computed(() => {
  return `https://player.vimeo.com/video/${props.videoId}?loop=false&autoplay=${props.autoplay}&muted=false&playsinline=true&byline=false&portrait=false&title=false&speed=true&transparent=false&&captions=false&pip=false&color=FDBD00&endscreen=void`
})
</script>

<template>
  <div class="flex h-full w-full">
    <iframe
      ref="player"
      :src="routeVideo"
      allowfullscreen
      class="h-full w-full"
      allow="autoplay; fullscreen; picture-in-picture; encrypted-media; accelerometer; gyroscope"
      :title="title"
    />
  </div>
</template>
